// National Weather Service (api.weather.gov) client for TWP's Milbank, SD
// location. Free, no API key. Client-side fetch, so no custom User-Agent
// header is set (browsers silently drop it) — NWS's public API works fine
// without one.
//
// Exposes a per-calendar-date lookup (condition code + temp only) so the
// Calendar page can render a small indicator inline in each day cell, rather
// than a separate current-conditions banner.

const LAT = 45.2183;
const LON = -96.6350;

const CACHE_KEY = 'twp_weather_by_date_v3';
const CACHE_TTL_MS = 20 * 60 * 1000; // 20 minutes — within the requested 15-30 min window

/** Maps NWS's free-text shortForecast (e.g. "Partly Cloudy", "Chance Rain
 *  Showers", "Patchy Fog then Mostly Sunny") to a simple condition code the
 *  UI layer maps to an icon. Checked most-specific/severe first so compound
 *  text ("...then Mostly Sunny") resolves to the more notable condition
 *  rather than the tail end. */
function conditionToCode(shortForecast = '') {
  const s = shortForecast.toLowerCase();
  if (/thunderstorm|tstm/.test(s)) return 'storm';
  if (/snow|flurries|sleet|wintry/.test(s)) return 'snow';
  if (/rain|shower|drizzle/.test(s)) return 'rain';
  if (/fog|mist|haze/.test(s)) return 'fog';
  if (/partly cloudy|partly sunny/.test(s)) return 'partly-cloudy';
  if (/cloudy|overcast/.test(s)) return 'cloudy';
  if (/wind/.test(s)) return 'wind';
  if (/mostly sunny|mostly clear|clear|sunny|fair/.test(s)) return 'sunny';
  return 'unknown';
}

function readCache(ignoreTTL = false) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data.fetchedAt) return null;
    if (!ignoreTTL && Date.now() - data.fetchedAt > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, fetchedAt: Date.now() }));
  } catch {}
}

async function fetchJSON(url) {
  const res = await fetch(url, { headers: { Accept: 'application/geo+json' } });
  if (!res.ok) throw new Error(`Weather fetch failed: ${res.status} ${url}`);
  return res.json();
}

async function fetchPlainJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Weather fetch failed: ${res.status} ${url}`);
  return res.json();
}

// ═══════════════════════════════════════════════════════════════════════
// HISTORICAL DAILY WEATHER (Open-Meteo — free, no API key required)
// Used for the Calendar page's "days worked" KPI to tell a truly idle day
// (crew available, nobody scheduled) apart from a weather-blocked day
// (crew couldn't have worked outside anyway), and for year-over-year
// comparisons. NWS's forecast API above only covers ~7 days forward, so
// it can't answer either of those — Open-Meteo's archive covers any past
// date range back to the 1940s, no signup needed.
// ═══════════════════════════════════════════════════════════════════════

// A day counts as weather-blocked for an exterior window-install crew if
// any of these hold. Thresholds are a business judgment call, not physics —
// adjust here if the actual crew's tolerance differs.
const BLOCK_SNOW_IN     = 1;    // inches of snowfall
const BLOCK_TEMP_MAX_F  = 20;   // day's high temp at or below this
const BLOCK_RAIN_IN     = 0.5;  // inches of rain/precip

function isBlockedDay({ tempMaxF, precipIn, snowIn }) {
  if (snowIn != null   && snowIn   >= BLOCK_SNOW_IN)    return true;
  if (tempMaxF != null && tempMaxF <= BLOCK_TEMP_MAX_F) return true;
  if (precipIn != null && precipIn >= BLOCK_RAIN_IN)    return true;
  return false;
}

function zipDaily(daily) {
  const out = {};
  const dates = daily?.time || [];
  dates.forEach((date, i) => {
    const tempMaxF = daily.temperature_2m_max?.[i] ?? null;
    const precipIn = daily.precipitation_sum?.[i] ?? null;
    const snowIn   = daily.snowfall_sum?.[i] ?? null;
    out[date] = { tempMaxF, precipIn, snowIn, blocked: isBlockedDay({ tempMaxF, precipIn, snowIn }) };
  });
  return out;
}

const HIST_CACHE_KEY = 'twp_weather_history_v1';
const HIST_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function readHistCache(key) {
  try {
    const all = JSON.parse(localStorage.getItem(HIST_CACHE_KEY) || '{}');
    const entry = all[key];
    if (!entry || Date.now() - entry.fetchedAt > HIST_CACHE_TTL_MS) return null;
    return entry.data;
  } catch { return null; }
}
function writeHistCache(key, data) {
  try {
    const all = JSON.parse(localStorage.getItem(HIST_CACHE_KEY) || '{}');
    all[key] = { data, fetchedAt: Date.now() };
    localStorage.setItem(HIST_CACHE_KEY, JSON.stringify(all));
  } catch {}
}

const DAILY_VARS = 'temperature_2m_max,precipitation_sum,snowfall_sum';

/** Per-date { tempMaxF, precipIn, snowIn, blocked } for the last `pastDays`
 *  days through today. Uses the forecast endpoint's past_days param (backed
 *  by recent-model data, not the ~5-day-lagged reanalysis archive) so
 *  "today" and yesterday are reliably included. */
export async function getMilbankRecentDailyWeather(pastDays) {
  const days = Math.max(1, Math.min(92, pastDays));
  const key = `recent_${days}`;
  const cached = readHistCache(key);
  if (cached) return cached;
  try {
    const data = await fetchPlainJSON(
      `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&daily=${DAILY_VARS}&temperature_unit=fahrenheit&precipitation_unit=inch&timezone=America%2FChicago&past_days=${days}&forecast_days=1`
    );
    const result = zipDaily(data.daily);
    writeHistCache(key, result);
    return result;
  } catch (err) {
    console.warn('Recent weather history fetch failed:', err);
    return {};
  }
}

/** Per-date { tempMaxF, precipIn, snowIn, blocked } for an arbitrary past
 *  date range (YYYY-MM-DD), e.g. the same month a year ago. */
export async function getMilbankArchivedDailyWeather(startDate, endDate) {
  const key = `archive_${startDate}_${endDate}`;
  const cached = readHistCache(key);
  if (cached) return cached;
  try {
    const data = await fetchPlainJSON(
      `https://archive-api.open-meteo.com/v1/archive?latitude=${LAT}&longitude=${LON}&start_date=${startDate}&end_date=${endDate}&daily=${DAILY_VARS}&temperature_unit=fahrenheit&precipitation_unit=inch&timezone=America%2FChicago`
    );
    const result = zipDaily(data.daily);
    writeHistCache(key, result);
    return result;
  } catch (err) {
    console.warn('Archived weather fetch failed:', err);
    return {};
  }
}

/** { 'YYYY-MM-DD': { tempF, condition, isDaytime } } — one entry per date the NWS
 *  forecast covers (~7-8 days out). Prefers the daytime period for a date
 *  when both day and night periods are present (a better "at a glance" temp
 *  than the overnight low); falls back to whichever period exists otherwise. */
function buildByDateLookup(periods) {
  const byDate = {};
  for (const p of periods) {
    const dateStr = (p.startTime || '').slice(0, 10);
    if (!dateStr) continue;
    const existing = byDate[dateStr];
    if (!existing || (p.isDaytime && !existing.isDaytime)) {
      byDate[dateStr] = {
        tempF: p.temperature,
        condition: conditionToCode(p.shortForecast),
        isDaytime: p.isDaytime,
      };
    }
  }
  return byDate;
}

/** Per-date { tempF, condition } lookup for Milbank, SD, covering whatever range
 *  the NWS forecast returns (~7-8 days from today). Cached in localStorage
 *  for CACHE_TTL_MS; on fetch failure falls back to the last cached value
 *  (marked `stale: true`) rather than throwing into the caller's render. */
export async function getMilbankForecastByDate({ force = false } = {}) {
  if (!force) {
    const cached = readCache();
    if (cached) return cached;
  }

  try {
    const point = await fetchJSON(`https://api.weather.gov/points/${LAT},${LON}`);
    const forecastUrl = point?.properties?.forecast;
    if (!forecastUrl) throw new Error('No forecast URL from NWS points endpoint');

    const forecastData = await fetchJSON(forecastUrl);
    const periods = forecastData?.properties?.periods || [];

    const result = {
      byDate: buildByDateLookup(periods),
      stale: false,
    };

    writeCache(result);
    return result;
  } catch (err) {
    const stale = readCache(true);
    if (stale) return { ...stale, stale: true };
    throw err;
  }
}
